import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { RegistrationFlowStep, STEP_ROUTES } from 'src/app/constants/registration-flow.constants';
import { RegistrationFlowService } from '../registration-flow/registration-flow.service';
import { UserService } from '../user/user.service';

/**
 * Sends users who have completed the owner details registration step on to their current step, or to the
 * dashboard once the registration is complete. Still lets them in when their address or phone number is
 * missing, since claim-pet sends them here to provide those.
 */
export const userDetailsStepGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const flowService = inject(RegistrationFlowService);
  const userService = inject(UserService);
  const router = inject(Router);

  return flowService.loadSteps().pipe(
    switchMap(() => {
      if (!flowService.isStepCompleted(RegistrationFlowStep.UserDetails)) {
        return of(true);
      }
      return userService.getMe().pipe(
        switchMap((user) => {
          if (!userService.areDetailsProvidedFor(user)) {
            return of(true);
          }
          // Reuses the steps loaded above instead of fetching them again
          return flowService.getResumeRoute(user, true).pipe(
            // A resume route back to this page would loop, so let them in
            map((route) =>
              route === STEP_ROUTES[RegistrationFlowStep.UserDetails] || router.parseUrl(route ?? '/dashboard')
            )
          );
        }),
        catchError(() => of(true))
      );
    })
  );
};
